import { API_ENDPOINTS, oauth_domain, oauth_key } from "../constants";
import type { Message } from "../contexts/chat-log/chat-log-context";
import type { InitializeResponse } from "../contexts/chatbot-config";

// tel:/mailto:/sms: リンクはwindow.open(target=_blank)だとモバイルSafariで
// ハンドラーアプリが正しく開かない場合があるため、location.hrefで遷移させる必要がある
const DIRECT_NAVIGATION_LINK_PATTERN = /^(tel:|mailto:|sms:)/i;

// ストリーミング中のAIメッセージを一意に識別するID発行用カウンター
// (Chat/Questionの両コンポーネントから複数の送信が並行してストリーミング
//  されても、それぞれの更新が自分自身のメッセージだけを更新するように
//  するため。単一の共有カウンターにすることで、両コンポーネントから
//  同時に発行されても衝突しない)
let aiMessageIdCounter = 0;
export function createAiMessageId(): string {
  aiMessageIdCounter += 1;
  return `ai-${Date.now()}-${aiMessageIdCounter}`;
}

export function isDirectNavigationLink(link: string): boolean {
  return DIRECT_NAVIGATION_LINK_PATTERN.test(link);
}

// tel:/mailto:/sms:はlocation.href、それ以外はwindow.open(_blank)で開く共通ハンドラー
export function openCtaLink(link: string) {
  if (isDirectNavigationLink(link)) {
    window.location.href = link;
    return;
  }
  window.open(link, "_blank", "noopener,noreferrer");
}

export function sleep(seconds: number) {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve("");
    }, seconds * 1000);
  });
}

// Cookie取得ユーティリティ関数
export function getCookie(name: string): string | null {
  let cookieValue = null;
  if (document.cookie && document.cookie !== "") {
    const cookies = document.cookie.split(";");
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.substring(0, name.length + 1) === name + "=") {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
        break;
      }
    }
  }
  return cookieValue;
}

// UUID形式のsession_idを生成する関数
export function generateUUID() {
  // RFC4122バージョン4に準拠したUUIDを生成
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0,
      v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// メモリ内セッションストレージ（sessionStorageが使えない場合の代替）
let memorySessionId: string | null = null;

// session_idを保存（ブラウザ新規起動時にリセット）
export function saveSessionId(session_id: string) {
  // メモリにも保存（フォールバック用）
  memorySessionId = session_id;

  try {
    if (typeof window !== 'undefined' && typeof sessionStorage !== 'undefined') {
      try {
        sessionStorage.setItem("session_id", session_id);
      } catch (e) {
        // sessionStorageアクセス不可の場合はメモリのみ使用
      }
    }
  } catch (e) {
    // sessionStorage自体が存在しない場合はメモリのみ使用
  }
  return session_id;
}

// session_idを取得
export function getSessionId() {
  try {
    if (typeof window !== 'undefined' && typeof sessionStorage !== 'undefined') {
      try {
        const stored = sessionStorage.getItem("session_id");
        if (stored) {
          memorySessionId = stored; // メモリにも同期
          return stored;
        }
      } catch (e) {
        // sessionStorageアクセス不可の場合はメモリから取得
      }
    }
  } catch (e) {
    // sessionStorage自体が存在しない場合はメモリから取得
  }
  // sessionStorageから取得できない場合はメモリから返す
  return memorySessionId;
}

// session_idと関連データをすべてクリア
export function clearSessionId() {
  // メモリもクリア
  memorySessionId = null;

  try {
    if (typeof window !== 'undefined' && typeof sessionStorage !== 'undefined') {
      try {
        sessionStorage.clear();
      } catch (e) {
        // sessionStorageアクセス不可の場合は何もしない
      }
    }
  } catch (e) {
    // sessionStorage自体が存在しない場合
  }
}

// 旧関数との互換性のため残す（内部的にsessionStorageを使用）
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function saveSessionIdWithExpire(hours: number) {
  // 型互換性のために引数を受け取るが未使用。コンパイラ警告を避けるため参照だけ行う。
  void hours;
  const session_id = generateUUID();
  return saveSessionId(session_id);
}

// 旧関数との互換性のため残す（内部的にsessionStorageを使用）
export function getSessionIdWithExpire() {
  return getSessionId();
}

// iframe内でsessionStorageが使えない場合の代替ストレージ
const memoryStorage: Map<string, string> = new Map();

// ストレージが使用可能かチェック（キャッシュして繰り返しアクセスを避ける）
let storageAvailableCache: boolean | null = null;
let storageCheckInProgress = false;

function isStorageAvailable(): boolean {
  // キャッシュがあればそれを返す
  if (storageAvailableCache !== null) {
    return storageAvailableCache;
  }

  // チェック中は false を返す（並行アクセスを防ぐ）
  if (storageCheckInProgress) {
    return false;
  }

  storageCheckInProgress = true;

  try {
    // sessionStorageオブジェクト自体の存在確認
    if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') {
      storageAvailableCache = false;
      storageCheckInProgress = false;
      return false;
    }

    const testKey = "__storage_test__";
    sessionStorage.setItem(testKey, "test");
    sessionStorage.removeItem(testKey);
    storageAvailableCache = true;
    storageCheckInProgress = false;
    return true;
  } catch (e) {
    // エラーをキャッチしてコンソールに出力しない
    storageAvailableCache = false;
    storageCheckInProgress = false;
    return false;
  }
}

// 安全なストレージ操作用のヘルパー関数
const safeStorage = {
  getItem: (key: string): string | null => {
    try {
      if (isStorageAvailable()) {
        return sessionStorage.getItem(key);
      }
    } catch (e) {
      // sessionStorageアクセスエラーの場合はメモリストレージにフォールバック
    }
    return memoryStorage.get(key) || null;
  },
  setItem: (key: string, value: string): void => {
    try {
      if (isStorageAvailable()) {
        sessionStorage.setItem(key, value);
        return;
      }
    } catch (e) {
      // sessionStorageアクセスエラーの場合はメモリストレージにフォールバック
    }
    memoryStorage.set(key, value);
  },
  removeItem: (key: string): void => {
    try {
      if (isStorageAvailable()) {
        sessionStorage.removeItem(key);
        return;
      }
    } catch (e) {
      // sessionStorageアクセスエラーの場合はメモリストレージにフォールバック
    }
    memoryStorage.delete(key);
  },
  get length(): number {
    try {
      if (isStorageAvailable()) {
        return sessionStorage.length;
      }
    } catch (e) {
      // sessionStorageアクセスエラーの場合はメモリストレージにフォールバック
    }
    return memoryStorage.size;
  },
  key: (index: number): string | null => {
    try {
      if (isStorageAvailable()) {
        return sessionStorage.key(index);
      }
    } catch (e) {
      // sessionStorageアクセスエラーの場合はメモリストレージにフォールバック
    }
    const keys = Array.from(memoryStorage.keys());
    return keys[index] || null;
  }
};

// フォームデータのストレージキーに使うテナント識別子。
// Shadow DOM越しに埋め込まれるこのウィジェットは、埋め込み先(顧客サイト)の
// オリジンのsessionStorageを共有するため、同一タブ内で複数テナントの
// ウィジェットが読み込まれた場合、session_id単体では名前空間として不十分
// （バックエンドの挙動やロード順によっては、両テナントが同じ/ session_id未確定の
// 期間に同じキーへアクセスし得る）。oauth_keyは非同期取得を待たず常に確定して
// いるため、これを名前空間に含めることでテナント間のフォームデータ混線を防ぐ。
const FORM_STORAGE_TENANT_NAMESPACE = oauth_key || "unknown-tenant";

// セッションIDが変更されたかチェックし、変更された場合は古いフォームデータをクリア
function checkAndCleanOldFormData() {
  try {
    const currentSessionId = getSessionId();
    const savedSessionIdKey = `form_session_id_${FORM_STORAGE_TENANT_NAMESPACE}`;
    const savedSessionId = safeStorage.getItem(savedSessionIdKey);

    if (currentSessionId && savedSessionId && currentSessionId !== savedSessionId) {
      // セッションIDが変更された場合、すべてのフォームデータをクリア
      clearAllFormInputs();
    }

    // 現在のセッションIDを保存
    if (currentSessionId) {
      safeStorage.setItem(savedSessionIdKey, currentSessionId);
    }
  } catch (error) {
    console.error("[Session] セッションチェックエラー:", error);
  }
}

function buildFormStorageKey(key: string): string {
  const sessionId = getSessionId();
  return sessionId
    ? `form_${FORM_STORAGE_TENANT_NAMESPACE}_${sessionId}_${key}`
    : `form_${FORM_STORAGE_TENANT_NAMESPACE}_${key}`;
}

// フォーム入力を保存（テナント+セッションID付き）
export function saveFormInput(key: string, value: string) {
  try {
    checkAndCleanOldFormData();
    safeStorage.setItem(buildFormStorageKey(key), value);
  } catch (error) {
    console.error("[Session] フォーム入力保存エラー:", error);
  }
}

// フォーム入力を取得（テナント+セッションID付き）
export function getFormInput(key: string): string {
  try {
    checkAndCleanOldFormData();
    return safeStorage.getItem(buildFormStorageKey(key)) || ""
  } catch (error) {
    console.error("[Session] フォーム入力取得エラー:", error);
    return "";
  }
}

// 特定のフォーム入力をクリア
export function clearFormInput(key: string) {
  try {
    safeStorage.removeItem(buildFormStorageKey(key));
  } catch (error) {
    console.error("[Session] フォーム入力クリアエラー:", error);
  }
}

// すべてのフォーム入力をクリア（セッション変更時に使用。現在のテナントの分のみ）
export function clearAllFormInputs() {
  try {
    const prefix = `form_${FORM_STORAGE_TENANT_NAMESPACE}_`;
    const keysToRemove: string[] = [];
    for (let i = 0; i < safeStorage.length; i++) {
      const key = safeStorage.key(i);
      if (key && key.startsWith(prefix)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => safeStorage.removeItem(key));
  } catch (error) {
    console.error("[Session] すべてのフォーム入力クリアエラー:", error);
  }
}

export async function sendMessage(
  message: string,
  onChunk?: (accumulatedText: string, newChunk: string) => void,
  onMetaUpdate?: (meta: {
    ai_question: string;
    answer: string[];
    campaign_text: string;
    cta_data: any[];
    display_link: any[];
    asset_data: any[];
    asset_description: string;
  }) => void,
  onReset?: (message?: string) => void
): Promise<Message> {
  try {
    const session_id = getSessionIdWithExpire();

    if (!oauth_key || !oauth_domain || !session_id) {
      throw new Error("oauth_key、oauth_domain、またはsession_idが未定義です");
    }

    const csrftoken = getCookie("csrftoken");

    const headers: HeadersInit = {
      "Content-Type": "application/x-www-form-urlencoded",
      // 明示的にSSEを要求し、UTF-8 を指定
      "Accept": "text/event-stream; charset=utf-8",
    };

    if (csrftoken) {
      headers["X-CSRFToken"] = csrftoken;
    }

    const response = await fetch(API_ENDPOINTS.push_message, {
      method: "POST",
      headers,
      body: new URLSearchParams({
        text: message,
        oauth_key,
        oauth_domain,
        session_id,
      }),
      // oauth_key/oauth_domainで認証する公開ウィジェットAPIのためCookie不要（バックエンドもこの経路にAllow-Credentialsを付与しない）
      credentials: 'omit',
    });

    if (!response.ok) {
      // サーバーエラーの詳細を取得
      let errorDetail = "";
      try {
        const errorText = await response.text();
        errorDetail = errorText.substring(0, 200); // 最初の200文字のみ
        console.error("サーバーエラーレスポンス:", errorDetail);
      } catch (e) {
        console.error("エラーレスポンスの読み取りに失敗:", e);
      }

      throw new Error(`HTTP エラー! ステータス: ${response.status}${errorDetail ? ` - ${errorDetail}` : ""}`);
    }

    // ストリーミングレスポンスを処理
    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error("ストリーミングレスポンスが利用できません");
    }

    // SSEのデフォルトはUTF-8だが、明示しておく
    const decoder = new TextDecoder('utf-8');
    let streamingText = ""; // ストリーミング中のテキスト部分
    let buffer = ""; // SSE行のバッファ
    let finalAnswer: string[] = [];
    let finalUrl: string[] = [];
    let finalDetailData: any[] = [];
    let finalAiQuestion = "";
    let finalAssetDescription = "";
    let finalCampaignText = "";
    let finalCtaData: any[] = [];
    let finalDisplayLink: any[] = [];
    let finalNeedCta = false;
    let lastAccumulatedText = "";
    let lastDisplayedText = "";

    try {
      while (true) {
        const { done, value } = await reader.read();

        // チャンクをデコード（ストリーム終了時は、decoder内部に保持されたままの
        // 末尾のマルチバイトUTF-8文字が破棄されないようflushする）
        const chunk = done ? decoder.decode() : decoder.decode(value, { stream: true });
        buffer += chunk;

        // 改行で分割してSSE行を処理
        const lines = buffer.split('\n');
        buffer = lines.pop() || ""; // 最後の不完全な行をバッファに保持

        for (const line of lines) {
          const trimmedLine = line.trim();

          // SSEの "data: " 行を処理
          if (trimmedLine.startsWith('data: ')) {
            try {
              const jsonStr = trimmedLine.substring(6);
              const eventData = JSON.parse(jsonStr);

              // セッション初期化要求の処理
              if (eventData.type === 'session_reset_required') {
                console.warn('🔄 セッション初期化要求を受信:', eventData.message);

                // consecutive_errors（旧）またはconsecutive_invalid_inputs（新）を表示
                const consecutiveCount = eventData.consecutive_invalid_inputs || eventData.consecutive_errors || 0;
                if (consecutiveCount > 0) {
                  console.warn(`連続検出回数: ${consecutiveCount}回`);
                }

                // ユーザーに通知メッセージを表示
                alert(eventData.message || '処理に問題があるため、会話を初期化します。');

                // セッションをクリアして新しいセッションIDを生成
                clearSessionId();
                const newSessionId = saveSessionIdWithExpire(24); // 24時間有効な新しいセッションIDを生成
                console.log(`新しいセッションID生成: ${newSessionId}`);

                // onReset コールバックを呼び出してチャットをクリア（ページリロードなし）
                // リセットメッセージを渡して最上段に残す
                if (onReset) {
                  onReset(eventData.message);
                }

                // セッションリセット時は空のメッセージを返す
                return {
                  model: '',
                  answer: [],
                  url: [],
                  asset_data: [],
                };
              }

              if (eventData.final_response) {
                if (Array.isArray(eventData.final_response.asset_data) && eventData.final_response.asset_data.length > 0) {
                  finalDetailData = eventData.final_response.asset_data;
                }
                // type="completed" なし（旧バックエンド等）でもフォールバック補完
                if (!finalCampaignText && typeof eventData.final_response.campaign_text === 'string' && eventData.final_response.campaign_text) {
                  finalCampaignText = eventData.final_response.campaign_text;
                }
                if (!finalAiQuestion && typeof eventData.final_response.ai_question === 'string' && eventData.final_response.ai_question) {
                  finalAiQuestion = eventData.final_response.ai_question;
                }
                if (!finalAssetDescription && typeof eventData.final_response.asset_description === 'string' && eventData.final_response.asset_description) {
                  finalAssetDescription = eventData.final_response.asset_description;
                }
                if (!finalAnswer.length && Array.isArray(eventData.final_response.answer)) {
                  finalAnswer = eventData.final_response.answer;
                }
                if (!finalCtaData.length && Array.isArray(eventData.final_response.cta_data)) {
                  finalCtaData = eventData.final_response.cta_data;
                }
                if (!finalDisplayLink.length && Array.isArray(eventData.final_response.display_link)) {
                  finalDisplayLink = eventData.final_response.display_link;
                }
                finalNeedCta = !!eventData.final_response.need_cta;
              }

              if (eventData.asset_data && Array.isArray(eventData.asset_data) && eventData.asset_data.length > 0) {
                finalDetailData = eventData.asset_data;
              }

              if (eventData.data && eventData.data.asset_data && Array.isArray(eventData.data.asset_data) && eventData.data.asset_data.length > 0) {
                finalDetailData = eventData.data.asset_data;
              }

              switch (eventData.type) {
                case "metadata":
                  break;

                case "text_chunk":
                  if (eventData.accumulated && eventData.accumulated !== lastAccumulatedText) {
                    const newText = eventData.accumulated;

                    streamingText = newText;
                    lastAccumulatedText = newText;

                    let displayText = newText;

                    if (displayText.includes('```json')) {
                      displayText = displayText.replace(/```json\n[\s\S]*?\n```/g, '').trim();
                    }

                    if (displayText.includes('```') && displayText.includes('{')) {
                      displayText = displayText.replace(/```[\s\S]*?```/g, '').trim();
                    }

                    displayText = displayText
                      .replace(/\\n/g, '\n')
                      .replace(/\r\n/g, '\n')
                      .replace(/\r/g, '\n');

                    if (onChunk) {
                      const newChunk = displayText.substring(lastDisplayedText.length);
                      onChunk(displayText, newChunk);
                      lastDisplayedText = displayText;
                    }
                  }
                  break;

                case "completed":
                  // asset_data収集
                  if (eventData.asset_data) {
                    if (Array.isArray(eventData.asset_data) && eventData.asset_data.length > 0) {
                      finalDetailData = eventData.asset_data;
                    }
                  }

                  if (eventData.data) {
                    if (eventData.data.asset_data) {
                      if (Array.isArray(eventData.data.asset_data) && eventData.data.asset_data.length > 0) {
                        finalDetailData = eventData.data.asset_data;
                      }
                    }
                  }

                  if (eventData.final_response) {
                    // 既存値がなければfinal_responseから設定（덮어쓰지 않기）
                    if (!finalAnswer.length && eventData.final_response.answer && Array.isArray(eventData.final_response.answer)) {
                      finalAnswer = eventData.final_response.answer;
                    }

                    if (!finalAiQuestion && eventData.final_response.ai_question && typeof eventData.final_response.ai_question === 'string') {
                      finalAiQuestion = eventData.final_response.ai_question;
                    }

                    if (!finalAssetDescription && eventData.final_response.asset_description && typeof eventData.final_response.asset_description === 'string') {
                      finalAssetDescription = eventData.final_response.asset_description;
                    }

                    if (!finalCampaignText && eventData.final_response.campaign_text && typeof eventData.final_response.campaign_text === 'string') {
                      finalCampaignText = eventData.final_response.campaign_text;
                    }

                    if (!finalCtaData.length && eventData.final_response.cta_data && Array.isArray(eventData.final_response.cta_data)) {
                      finalCtaData = eventData.final_response.cta_data;
                    }

                    if (!finalDisplayLink.length && eventData.final_response.display_link && Array.isArray(eventData.final_response.display_link)) {
                      finalDisplayLink = eventData.final_response.display_link;
                    }

                    if (eventData.final_response.asset_data) {
                      if (Array.isArray(eventData.final_response.asset_data) && eventData.final_response.asset_data.length > 0) {
                        finalDetailData = eventData.final_response.asset_data;
                      }
                    }
                  }

                  // JSONパース処理を即座に実行（whileループ終了後に移動）
                  // ストリーミング終了を通知（ループ終了後に処理）
                  break;

                case "text_completed":
                  if (eventData.asset_data && Array.isArray(eventData.asset_data) && eventData.asset_data.length > 0) {
                    finalDetailData = eventData.asset_data;
                  }
                  if (eventData.data && eventData.data.asset_data && Array.isArray(eventData.data.asset_data) && eventData.data.asset_data.length > 0) {
                    finalDetailData = eventData.data.asset_data;
                  }
                  break;

                case "answers":
                  if (eventData.content && Array.isArray(eventData.content)) {
                    finalAnswer = eventData.content;
                  } else if (eventData.answers && Array.isArray(eventData.answers)) {
                    finalAnswer = eventData.answers;
                  } else if (eventData.data && Array.isArray(eventData.data)) {
                    finalAnswer = eventData.data;
                  }

                  if (eventData.asset_data && Array.isArray(eventData.asset_data) && eventData.asset_data.length > 0) {
                    finalDetailData = eventData.asset_data;
                  }
                  if (eventData.data && eventData.data.asset_data && Array.isArray(eventData.data.asset_data) && eventData.data.asset_data.length > 0) {
                    finalDetailData = eventData.data.asset_data;
                  }
                  break;

                case "ai_question":
                  if (eventData.content && typeof eventData.content === 'string') {
                    finalAiQuestion = eventData.content;
                  } else if (eventData.ai_question && typeof eventData.ai_question === 'string') {
                    finalAiQuestion = eventData.ai_question;
                  }
                  break;

                case "campaign_text":
                  if (eventData.content && typeof eventData.content === 'string') {
                    finalCampaignText = eventData.content;
                  } else if (eventData.campaign_text && typeof eventData.campaign_text === 'string') {
                    finalCampaignText = eventData.campaign_text;
                  }
                  break;

                case "cta_data":
                  if (eventData.content && Array.isArray(eventData.content)) {
                    finalCtaData = eventData.content;
                  } else if (eventData.cta_data && Array.isArray(eventData.cta_data)) {
                    finalCtaData = eventData.cta_data;
                  }
                  break;

                case "display_link":
                  if (eventData.content && Array.isArray(eventData.content)) {
                    finalDisplayLink = eventData.content;
                  }
                  break;

                case "asset_data":
                case "reference_data":
                case "media":
                  if (eventData.content && Array.isArray(eventData.content)) {
                    finalDetailData = eventData.content;
                  } else if (eventData.asset_data && Array.isArray(eventData.asset_data)) {
                    finalDetailData = eventData.asset_data;
                  } else if (eventData.data && Array.isArray(eventData.data)) {
                    finalDetailData = eventData.data;
                  }
                  break;

                default:
                  break;
              }

              // 各フィールドが解析され次第、即座に呼び出し元へ通知する（本文と同時に届く順に画面へ反映するため）
              if (onMetaUpdate) {
                onMetaUpdate({
                  ai_question: finalAiQuestion,
                  answer: finalAnswer,
                  campaign_text: finalCampaignText,
                  cta_data: finalCtaData,
                  display_link: finalDisplayLink,
                  asset_data: finalDetailData,
                  asset_description: finalAssetDescription,
                });
              }

            } catch (parseError) {
              console.warn("SSE行の解析エラー:", parseError, "行:", trimmedLine);
            }
          }
        }

        if (done) {
          break;
        }
      }

    } finally {
      reader.releaseLock();
    }

    // whileループ終了後、JSON解析を実行
    let errorCode = 0; // デフォルトはエラーなし

    if (streamingText.includes('```json')) {
      const jsonMatch = streamingText.match(/```json\n([\s\S]*?)\n```/);
      if (jsonMatch && jsonMatch[1]) {
        try {
          const finalData = JSON.parse(jsonMatch[1]);

          if (finalData.event && typeof finalData.event.error_code === 'number') {
            errorCode = finalData.event.error_code;
          }
          if (finalData.answer && Array.isArray(finalData.answer)) {
            finalAnswer = finalData.answer;
          }
          if (finalData.asset_data && Array.isArray(finalData.asset_data)) {
            finalDetailData = finalData.asset_data;
          }
        } catch (jsonParseError) {
          console.warn("最終JSONデータ解析エラー:", jsonParseError);
        }
      }

      streamingText = streamingText.replace(/```json\n[\s\S]*?\n```/g, '').trim();
    }

    let jsonStartIndex = streamingText.indexOf('{{');
    let isDoublebraceFormat = true;

    if (jsonStartIndex === -1) {
      jsonStartIndex = streamingText.lastIndexOf('{');
      isDoublebraceFormat = false;
    }

    if (jsonStartIndex !== -1) {
      const textBeforeJson = streamingText.substring(0, jsonStartIndex).trim();
      const jsonPart = streamingText.substring(jsonStartIndex);
      let braceCount = 0;
      let jsonEndIndex = -1;

      for (let i = 0; i < jsonPart.length; i++) {
        const char = jsonPart[i];
        if (char === '{') {
          braceCount++;
        } else if (char === '}') {
          braceCount--;
          if (braceCount === 0) {
            jsonEndIndex = i;
            break;
          }
        }
      }

      if (jsonEndIndex !== -1) {
        const jsonString = jsonPart.substring(0, jsonEndIndex + 1);
        try {
          let cleanJson = jsonString;
          if (isDoublebraceFormat) {
            cleanJson = jsonString.replace(/^\{\{/, '{').replace(/\}\}$/, '}');
          }
          const parsedJson = JSON.parse(cleanJson);

          if (parsedJson && typeof parsedJson === 'object') {
            if (parsedJson.event && typeof parsedJson.event.error_code === 'number') {
              errorCode = parsedJson.event.error_code;
            }
            if (Array.isArray(parsedJson.answer)) {
              finalAnswer = parsedJson.answer;
            }
            if (Array.isArray(parsedJson.asset_data)) {
              finalDetailData = parsedJson.asset_data;
            }
          }
        } catch (jsonParseError) {
          console.warn("通常JSONパターン解析エラー:", jsonParseError);
        }
      }

      streamingText = textBeforeJson;
    }

    if (streamingText.includes('```') && streamingText.includes('{')) {
      streamingText = streamingText.replace(/```[\s\S]*?```/g, '').trim();
    }

    let finalModel;
    // streamingTextのトリミング後の内容をチェック
    const trimmedStreamingText = streamingText ? streamingText.trim() : "";

    if (trimmedStreamingText.length > 0) {
      finalModel = streamingText; // 元の改行やフォーマットを保持
    } else if (finalAnswer && finalAnswer.length > 0) {
      finalModel = "ご質問に関連する情報をご用意しました。下記の推奨質問もご参考ください。";
    } else if (finalDetailData && finalDetailData.length > 0) {
      // テキストはないがasset_dataがある場合
      finalModel = "ご質問に関連する資料をご用意しました。";
    } else {
      // 何も情報がない場合（サーバーエラーや通信エラーの可能性）
      finalModel = "申し訳ありません、応答を受信できませんでした。もう一度お試しください。";
    }

    const finalResponse = {
      event: {
        error_code: errorCode,
      },
      model: finalModel,
      answer: finalAnswer,
      url: finalUrl,
      asset_data: finalDetailData,
      ai_question: finalAiQuestion,
      asset_description: finalAssetDescription,
      campaign_text: finalCampaignText,
      cta_data: finalCtaData,
      display_link: finalDisplayLink,
      need_cta: finalNeedCta,
    };

    return finalResponse;

  } catch (error) {
    console.error("Fetch エラー:", error);
    console.error("エラー詳細:", {
      message: error instanceof Error ? error.message : String(error),
      oauth_key: oauth_key ? "設定済み" : "未設定",
      oauth_domain: oauth_domain || "未設定",
      session_id: getSessionIdWithExpire() ? "設定済み" : "未設定",
    });

    // エラーの種類に応じたメッセージを返す
    let errorMessage = "申し訳ありません、応答を正しく処理できませんでした。";

    if (error instanceof Error) {
      if (error.message.includes("oauth_key") || error.message.includes("oauth_domain") || error.message.includes("session_id")) {
        errorMessage = "設定エラー: チャットボットの設定が正しくありません。ページを再読み込みしてください。";
      } else if (error.message.includes("ストリーミング")) {
        errorMessage = "通信エラー: データの受信に失敗しました。再度お試しください。";
      }
    }

    return {
      model: errorMessage,
      answer: [],
      url: [],
      asset_data: [],
      ai_question: "",
      asset_description: "",
      campaign_text: "",
      cta_data: [],
      display_link: [],
    };
  }
}

// リンククリックイベントをフックしてlead_acquisition APIに送信する関数
export async function trackLinkClick(url: string): Promise<void> {
  try {
    const session_id = getSessionIdWithExpire();

    if (!oauth_key || !oauth_domain || !session_id) {
      return;
    }

    let linkType = "URL";
    if (url.startsWith("tel:")) {
      linkType = "電話";
    } else if (url.startsWith("mailto:")) {
      linkType = "メール";
    } else if (url.startsWith("sms:")) {
      linkType = "SMS";
    } else if (url.startsWith("http://") || url.startsWith("https://")) {
      linkType = "外部リンク";
    } else if (url.startsWith("/") || !url.includes(":")) {
      linkType = "内部リンク";
    }

    const requestBody = {
      oauth_key,
      oauth_domain,
      session_id,
      type: linkType,
      url: url,
    };

    const csrftoken = getCookie("csrftoken");

    const headers: HeadersInit = {
      "Content-Type": "application/json",
    };

    if (csrftoken) {
      headers["X-CSRFToken"] = csrftoken;
    }

    const response = await fetch(API_ENDPOINTS.lead_acquisition, {
      method: "POST",
      headers,
      body: JSON.stringify(requestBody),
      // oauth_key/oauth_domainで認証する公開ウィジェットAPIのためCookie不要
      credentials: 'omit',
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.warn(`リンク追跡失敗: HTTP ${response.status}`, errorText);
    } else {
      await response.json();
    }
  } catch (error) {
    console.error("リンク追跡エラー:", error);
  }
}

/**
 * DOMに存在するすべてのリンクにトラッキング機能を追加
 * iframe環境での動作を考慮
 */
export function setupLinkTracking(containerElement: HTMLElement = document.body) {
  // 既存のリンクにイベントリスナーを追加
  const links = containerElement.querySelectorAll('a[href]');

  links.forEach(link => {
    if ((link as any).__linkTrackingAdded) return; // 重複防止

    link.addEventListener('click', async () => {
      const href = link.getAttribute('href');
      if (!href || href === '#' || href.startsWith('javascript:')) return;

      // target="_blank"とrel属性を設定（外部リンクの場合）
      if (href.startsWith('http://') || href.startsWith('https://')) {
        link.setAttribute('target', '_blank');
        link.setAttribute('rel', 'noopener noreferrer');
      }

      try {
        trackLinkClick(href).catch(error => {
          console.warn(`リンクトラッキング送信エラー (${href}):`, error);
        });
      } catch (error) {
        console.warn(`リンクトラッキング送信エラー (${href}):`, error);
      }
    });

    // 重複防止フラグ
    (link as any).__linkTrackingAdded = true;
  });

  // ボタンやその他の要素でonclick属性を持つものも追跡
  const clickableElements = containerElement.querySelectorAll('[onclick], button[data-url], div[data-url]');
  clickableElements.forEach(element => {
    if ((element as any).__linkTrackingAdded) return;

    element.addEventListener('click', async () => {
      const dataUrl = element.getAttribute('data-url');
      if (dataUrl) {
        try {
          trackLinkClick(dataUrl).catch(error => {
            console.warn(`ボタンクリックトラッキング送信エラー (${dataUrl}):`, error);
          });
        } catch (error) {
          console.warn(`ボタンクリックトラッキング送信エラー:`, error);
        }
      }
    });

    (element as any).__linkTrackingAdded = true;
  });
}

export async function initializeChat(url: string): Promise<{ message: Message; fullResponse: InitializeResponse | null }> {
  try {
    // 既存のセッションをクリア（ブラウザリフレッシュ時に新しいセッションを作成）
    let session_id = getSessionId();

    if (!session_id) {
      // セッションIDがない場合は空文字列で送信
      session_id = "";
    }

    if (!oauth_key || !oauth_domain) {
      throw new Error(
        "oauth_key、oauth_domainが未定義です"
      );
    }

    const csrftoken = getCookie("csrftoken");

    const headers: HeadersInit = {
      "Content-Type": "application/json",
    };

    if (csrftoken) {
      headers["X-CSRFToken"] = csrftoken;
    }

    const body = JSON.stringify({
      oauth_key,
      oauth_domain,
      session_id,
      url: url,
    });

    console.log("[Initialize API] 送信データ:", {
      oauth_key,
      oauth_domain,
      session_id,
      url,
      endpoint: API_ENDPOINTS.initialize
    });

    const response = await fetch(API_ENDPOINTS.initialize, {
      method: "POST",
      headers,
      body,
      // oauth_key/oauth_domainで認証する公開ウィジェットAPIのためCookie不要
      credentials: 'omit',
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error("[Initialize API] エラーレスポンス:", errorText);
      throw new Error(`HTTP エラー! ステータス: ${response.status}`);
    }

    const data: InitializeResponse = await response.json();

    // チャットボット停止・月間制限超過・サービス解約済みの場合、停止フラグを含めて返す
    if (data.is_bot_active === false || data.is_service_cancelled === true) {
      console.warn("[Initialize API] チャットボット停止中またはサービス解約済み (is_bot_active: false / is_service_cancelled: true)");
      return {
        message: {
          model: data.message || "",
          answer: [],
          url: [],
          asset_data: [],
          ai_question: "",
          campaign_text: "",
          cta_data: [],
        },
        fullResponse: data,
      };
    }

    console.log("[Initialize API] レスポンス受信成功:", {
      session_id: data.session_id,
      model_length: data.model?.length || 0,
      answer_count: data.answer?.length || 0,
      answers: data.answer || [],

      // CTA Data
      has_cta_data: !!data.cta_data,
      cta_title: data.cta_data?.title || '',
      cta_items_count: data.cta_data?.cta_data?.length || 0,
      cta_items: data.cta_data?.cta_data?.map(item => ({
        cta_sort: item.cta_sort,
        title: item.title,
        type: item.type,
        download_link_count: item.download_link?.length || 0
      })) || [],

      // Bottom View Data (フォームデータ)
      has_bottom_view_data: !!data.bottom_view_data,
      bottom_view_title: (data.bottom_view_data as any)?.title || '',
      bottom_view_items_count: (data.bottom_view_data as any)?.data?.length || 0,
      bottom_view_items: (data.bottom_view_data as any)?.data?.map((item: any) => ({
        data_sort: item.data_sort,
        title: item.title,
        type: item.type,
        dl_type: item.dl_type,
        form_title: item.form_title,
        link: item.link,
        image: item.image,
        page_count: item.page_details?.length || 0,
        page_details: item.page_details?.map((p: any) => ({
          title: p.title,
          type: p.type,
          form_text: p.form_text,
          required: p.required
        })) || []
      })) || [],

      // Chatbot Data
      has_chatbot_data: !!data.chatbot_data,
      chatbot_title: data.chatbot_data?.chatbot_title || '',
      chatbot_plan: data.chatbot_data?.chatbot_plan || '',
      has_subscription: data.chatbot_data?.subscription_info?.has_subscription || false,
      subscription_plans_count: data.chatbot_data?.subscription_info?.plans?.length || 0,

      // Screen2 Data
      screen2_data_count: (data.screen2_data as any)?.length || 0,
      screen2_items: (data.screen2_data as any)?.map((item: any) => ({
        sort_num: item.sort_num,
        title: item.title,
        type: item.type
      })) || []
    });

    // APIから返されたセッションIDを保存
    if (data.session_id) {
      saveSessionId(data.session_id);
    } else {
      console.warn("[Session] 初期化APIレスポンスにセッションIDが含まれていません");
    }

    if (!data.bottom_view_data) {
      console.warn("bottom_view_dataがAPIレスポンスに含まれていません");
      console.warn("これによりBottomSheetのフォームが表示されない可能性があります");
      console.warn("テナント設定を確認してください: oauth_domain =", oauth_domain);
    }

    const message: Message = {
      model: data.model,
      answer: data.answer || [],
      url: [],
      asset_data: [],
      ai_question: "",
      campaign_text: "",
      cta_data: [],
    };

    return {
      message,
      fullResponse: data,
    };
  } catch (error) {
    console.error("Fetch エラー:", error);

    const message: Message = {
      model: "申し訳ありません、チャットを初期化できませんでした。",
      answer: [],
      url: [],
      asset_data: [],
      ai_question: "",
      campaign_text: "",
      cta_data: [],
    };

    return {
      message,
      fullResponse: null,
    };
  }
}

/**
 * 与えられたHTML文字列内のテキストノードからメールアドレスと電話番号を検出し、
 * <a href="mailto:"> と <a href="tel:"> のリンクに安全に変換します。
 * 既存の a / code / pre タグ内部は変更しません。
 * ブラウザ環境での実行を前提とします（テストでは jsdom 等が必要）。
 */
export function linkifyEmailsAndPhonesInHtml(html: string): string {
  if (!html || typeof window === 'undefined' || typeof document === 'undefined') {
    return html;
  }

  // 改行文字を<br>タグに変換
  const htmlWithBreaks = html.replace(/\n/g, '<br>');

  const container = document.createElement('div');
  container.innerHTML = htmlWithBreaks;

  const shouldSkip = (node: Node): boolean => {
    let current: Node | null = node;
    while (current) {
      if (current.nodeType === Node.ELEMENT_NODE) {
        const tag = (current as Element).tagName.toLowerCase();
        if (tag === 'a' || tag === 'code' || tag === 'pre' || tag === 'script' || tag === 'style') {
          return true;
        }
      }
      current = current.parentNode;
    }
    return false;
  };

  // 比較的保守的な電話番号判定（日本/韓国などの一般的形式をカバー）。
  // 条件:
  //  - 国際形式: + から始まり、7桁以上の数字を含む
  //  - 国内形式: 0 から始まり、 (2-4桁)-(3-4桁)-(4桁) のような形式
  const phoneIntl = /\+(?:[\d][\d\s().\-\u3000\u2212\u2013\u2014]{5,}\d)/g; // +82-10-1234-5678 など（全角空白やダッシュ類も許容）
  // 国内番号: 先頭0 + (1〜3桁) + 区切り + (3〜4桁) + 区切り + (4桁)
  // 0800 や 0120 といった4桁先頭ブロックもカバーするため \d{1,3} に拡張
  // 区切りには -, ., 半角/全角空白, 各種ダッシュを許容
  const phoneDomestic = /\b0\d{1,3}[-.\s\u3000\u2212\u2013\u2014]?\d{3,4}[-.\s\u3000\u2212\u2013\u2014]?\d{4}\b/g; // 0800-800-4977, 010-1234-5678, 02-123-4567
  const emailPattern = /\b[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.-]+\.[A-Za-z]{2,}\b/g;

  const normalizeTelHref = (raw: string): string => {
    // href 用に数字と先頭の + のみを残す
    let cleaned = raw.trim();
    const hasPlus = cleaned.startsWith('+');
    cleaned = cleaned.replace(/[^\d+]/g, '');
    if (hasPlus) {
      // 先頭以外の + を削除
      cleaned = '+' + cleaned.replace(/\+/g, '');
    } else {
      // 先頭以外の + をすべて削除
      cleaned = cleaned.replace(/\+/g, '');
    }
    return cleaned;
  };

  const processTextNode = (node: Text) => {
    const text = node.nodeValue || '';
    // まずメールを優先的にリンク化（@ を含むパターンは電話と競合しづらいため）
    const parts: (string | HTMLAnchorElement)[] = [];

    const pushWithEmailLinks = (input: string) => {
      let lastIndex = 0;
      for (const match of input.matchAll(emailPattern)) {
        const m = match[0];
        const start = match.index ?? 0;
        const end = start + m.length;
        if (start > lastIndex) parts.push(input.slice(lastIndex, start));
        const a = document.createElement('a');
        a.href = `mailto:${m}`;
        a.textContent = m;
        parts.push(a);
        lastIndex = end;
      }
      if (lastIndex < input.length) parts.push(input.slice(lastIndex));
    };

    pushWithEmailLinks(text);

    // 次に電話番号をリンク化。既に生成した a 要素はスキップし、文字列部分のみ処理。
    const finalNodes: Node[] = [];
    const linkifyPhonesInString = (str: string): Node[] => {
      const nodes: Node[] = [];
      // 2種類のパターンを順に適用し、オーバーラップを避けるため位置で処理
      // ここでは簡便のため、1つの大きなパターンで全探索
      const combined = new RegExp(`${phoneIntl.source}|${phoneDomestic.source}`, 'g');
      let lastIndex = 0;
      for (const match of str.matchAll(combined)) {
        const m = match[0];
        const start = match.index ?? 0;
        const end = start + m.length;
        // 保守的フィルタ: 数字合計が7桁未満は除外
        const digitCount = (m.match(/\d/g) || []).length;
        if (digitCount < 7) continue;
        if (start > lastIndex) nodes.push(document.createTextNode(str.slice(lastIndex, start)));
        const a = document.createElement('a');
        a.href = `tel:${normalizeTelHref(m)}`;
        a.textContent = m;
        nodes.push(a);
        lastIndex = end;
      }
      if (lastIndex < str.length) nodes.push(document.createTextNode(str.slice(lastIndex)));
      return nodes;
    };

    // parts をノードに展開し、文字列には電話リンクを適用
    parts.forEach((part) => {
      if (typeof part === 'string') {
        finalNodes.push(...linkifyPhonesInString(part));
      } else {
        finalNodes.push(part);
      }
    });

    if (finalNodes.length === 0) return;

    const parent = node.parentNode;
    if (!parent) return;
    finalNodes.forEach((n) => parent.insertBefore(n, node));
    parent.removeChild(node);
  };

  const walker = (root: Node) => {
    const it = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    const toProcess: Text[] = [];
    let current: Node | null = it.nextNode();
    while (current) {
      if (current.nodeType === Node.TEXT_NODE && current.nodeValue && current.nodeValue.trim()) {
        if (!shouldSkip(current)) {
          toProcess.push(current as Text);
        }
      }
      current = it.nextNode();
    }
    // 収集後に処理（ライブツリー更新の副作用回避）
    toProcess.forEach(processTextNode);
  };

  walker(container);
  return container.innerHTML;
}
